import React, { useEffect, useState } from 'react';
import { Flower2, Heart, Shield, Bell, Plus, Trash2, Clock, Pill, Calendar, Check, TrendingUp, Award, CheckCircle2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import BottomNav from '../components/BottomNav';
import { modulesAPI, contraceptionAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const Health = () => {
  const [healthModules, setHealthModules] = useState([]);
  const [contraception, setContraception] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [takingPill, setTakingPill] = useState(false);
  const [showContraceptionDialog, setShowContraceptionDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [contraceptionForm, setContraceptionForm] = useState({
    type: 'pill',
    renewalDate: new Date().toISOString().split('T')[0],
    endDate: '',
    time: '09:00'
  });

  const fetchData = async () => {
    try {
      const [modulesRes, contraceptionRes, remindersRes, statsRes] = await Promise.all([
        modulesAPI.getAll('health'),
        contraceptionAPI.get(),
        api.get('/reminders'),
        contraceptionAPI.getStats().catch(() => ({ data: { configured: false } }))
      ]);
      
      setHealthModules(modulesRes.data.modules || []);
      setContraception(contraceptionRes.data.contraception);
      setReminders(remindersRes.data.reminders || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching health data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recordPillTaken = async () => {
    setTakingPill(true);
    try {
      const res = await contraceptionAPI.recordTaken({
        takenAt: new Date().toISOString()
      });
      
      if (res.data.alreadyTaken) {
        toast({
          title: 'Déjà enregistré',
          description: 'Vous avez déjà confirmé votre prise aujourd\'hui'
        });
      } else {
        toast({
          title: 'Prise confirmée!',
          description: `Série actuelle: ${res.data.streak} jour(s)`
        });
        
        // Refresh stats
        const statsRes = await contraceptionAPI.getStats();
        setStats(statsRes.data);
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible d\'enregistrer',
        variant: 'destructive'
      });
    } finally {
      setTakingPill(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await contraceptionAPI.getHistory(30);
      setHistory(res.data.calendar || []);
      setShowHistoryDialog(true);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger l\'historique',
        variant: 'destructive'
      });
    }
  };

  const saveContraception = async () => {
    try {
      // Auto-detect user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Abidjan';
      const dataWithTimezone = { ...contraceptionForm, timezone: userTimezone };
      
      if (contraception) {
        await contraceptionAPI.update(contraception.id, dataWithTimezone);
      } else {
        await contraceptionAPI.create(dataWithTimezone);
      }
      
      const res = await contraceptionAPI.get();
      setContraception(res.data.contraception);
      setShowContraceptionDialog(false);
      
      toast({
        title: 'Succès',
        description: 'Contraception enregistrée'
      });
      
      // Setup reminders
      await api.post('/reminders/setup');
      const remindersRes = await api.get('/reminders');
      setReminders(remindersRes.data.reminders || []);
      
      // Refresh stats
      const statsRes = await contraceptionAPI.getStats();
      setStats(statsRes.data);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder',
        variant: 'destructive'
      });
    }
  };

  const deleteContraception = async () => {
    if (!contraception) return;
    
    try {
      await contraceptionAPI.delete(contraception.id);
      setContraception(null);
      setStats(null);
      toast({
        title: 'Supprimé',
        description: 'Contraception supprimée'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer',
        variant: 'destructive'
      });
    }
  };

  const acknowledgeReminder = async (reminderId) => {
    try {
      await api.post(`/reminders/${reminderId}/acknowledge`);
      setReminders(reminders.filter(r => r.id !== reminderId));
      toast({
        title: 'Rappel confirmé'
      });
    } catch (error) {
      console.error('Error acknowledging reminder:', error);
    }
  };

  const getContraceptionLabel = (type) => {
    const labels = {
      pill: 'Pilule',
      patch: 'Patch',
      ring: 'Anneau',
      injection: 'Injection',
      implant: 'Implant',
      iud: 'Stérilet (DIU)'
    };
    return labels[type] || type;
  };

  const getContraceptionIcon = (type) => {
    switch (type) {
      case 'pill': return <Pill className="w-6 h-6" />;
      case 'patch': return <Shield className="w-6 h-6" />;
      default: return <Shield className="w-6 h-6" />;
    }
  };

  const getModuleIcon = (icon) => {
    return <span className="text-4xl">{icon}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center gap-3">
          <Flower2 className="w-8 h-8 text-pink-300" />
          <h1 className="text-2xl font-bold text-white">Santé</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Header Info */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-400 to-purple-600 flex items-center justify-center">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Santé Intime</h2>
          <p className="text-pink-200 text-sm">
            Gérez votre contraception et vos rappels en toute simplicité.
          </p>
        </div>

        {/* Contraception Card */}
        <Card className="p-5 bg-gradient-to-r from-purple-600 to-pink-600 border-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white">
                {contraception ? getContraceptionIcon(contraception.type) : <Shield className="w-6 h-6" />}
              </div>
              <div>
                <h3 className="text-white font-bold">Contraception</h3>
                {contraception ? (
                  <p className="text-white/80 text-sm">{getContraceptionLabel(contraception.type)}</p>
                ) : (
                  <p className="text-white/80 text-sm">Non configurée</p>
                )}
              </div>
            </div>
            
            <Dialog open={showContraceptionDialog} onOpenChange={setShowContraceptionDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  {contraception ? 'Modifier' : <Plus className="w-4 h-4" />}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-purple-900 border-purple-700">
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {contraception ? 'Modifier' : 'Ajouter'} ma contraception
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label className="text-pink-200">Type de contraception</Label>
                    <Select 
                      value={contraceptionForm.type} 
                      onValueChange={(value) => setContraceptionForm({...contraceptionForm, type: value})}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-purple-800 border-purple-600">
                        <SelectItem value="pill">Pilule (quotidienne)</SelectItem>
                        <SelectItem value="patch">Patch (hebdomadaire)</SelectItem>
                        <SelectItem value="ring">Anneau vaginal (mensuel)</SelectItem>
                        <SelectItem value="injection">Injection (trimestrielle)</SelectItem>
                        <SelectItem value="implant">Implant</SelectItem>
                        <SelectItem value="iud">Stérilet (DIU)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-pink-200">Date de début / renouvellement</Label>
                    <Input
                      type="date"
                      value={contraceptionForm.renewalDate}
                      onChange={(e) => setContraceptionForm({...contraceptionForm, renewalDate: e.target.value})}
                      className="bg-white/10 border-white/20 text-white mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-pink-200">Date de fin (optionnel)</Label>
                    <Input
                      type="date"
                      value={contraceptionForm.endDate}
                      onChange={(e) => setContraceptionForm({...contraceptionForm, endDate: e.target.value})}
                      className="bg-white/10 border-white/20 text-white mt-1"
                      placeholder="Laisser vide si pas de date de fin"
                    />
                    <p className="text-pink-300 text-xs mt-1">Laissez vide pour un suivi continu</p>
                  </div>
                  
                  {(contraceptionForm.type === 'pill' || contraceptionForm.type === 'patch') && (
                    <div>
                      <Label className="text-pink-200">Heure du rappel</Label>
                      <Input
                        type="time"
                        value={contraceptionForm.time}
                        onChange={(e) => setContraceptionForm({...contraceptionForm, time: e.target.value})}
                        className="bg-white/10 border-white/20 text-white mt-1"
                      />
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button onClick={saveContraception} className="flex-1 bg-pink-500 hover:bg-pink-600">
                      Enregistrer
                    </Button>
                    {contraception && (
                      <Button onClick={deleteContraception} variant="destructive" size="icon">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {contraception && (
            <div className="space-y-2 text-white/80 text-sm">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{contraception.daysRemaining} jours restants</span>
                </div>
                {contraception.time && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{contraception.time}</span>
                  </div>
                )}
              </div>
              {contraception.endDate && (
                <div className="flex items-center gap-1 text-yellow-300">
                  <Calendar className="w-4 h-4" />
                  <span>Fin prévue: {new Date(contraception.endDate).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Quick Action: Take Pill Button */}
        {contraception && (contraception.type === 'pill' || contraception.type === 'patch') && (
          <Card className={`p-4 border-0 ${
            stats?.todayTaken 
              ? 'bg-gradient-to-r from-green-600 to-emerald-600' 
              : 'bg-gradient-to-r from-pink-500 to-rose-500'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  stats?.todayTaken ? 'bg-green-400/30' : 'bg-white/20'
                }`}>
                  {stats?.todayTaken ? (
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  ) : (
                    <Pill className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-white font-bold">
                    {stats?.todayTaken ? 'Prise confirmée!' : `J'ai pris ma ${getContraceptionLabel(contraception.type).toLowerCase()}`}
                  </h3>
                  <p className="text-white/80 text-sm">
                    {stats?.todayTaken 
                      ? `Série: ${stats?.currentStreak || 0} jour(s)` 
                      : 'Confirmez votre prise quotidienne'}
                  </p>
                </div>
              </div>
              
              {!stats?.todayTaken && (
                <Button 
                  onClick={recordPillTaken}
                  disabled={takingPill}
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  {takingPill ? (
                    <span className="animate-pulse">...</span>
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Adherence Statistics */}
        {stats?.configured && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-pink-300" />
                <h3 className="text-lg font-bold text-white">Statistiques</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={loadHistory}
                className="text-pink-300 hover:text-white hover:bg-white/10"
              >
                Historique
              </Button>
            </div>
            
            {/* Current Streak */}
            <div className="flex items-center gap-4 mb-4 p-3 rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20">
              <div className="w-12 h-12 rounded-full bg-yellow-500/30 flex items-center justify-center">
                <Award className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-white font-bold text-xl">{stats.currentStreak} jour(s)</p>
                <p className="text-yellow-200 text-sm">Série actuelle</p>
              </div>
            </div>

            {/* Weekly Stats */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-pink-200">Cette semaine</span>
                  <span className="text-white font-medium">{stats.weekStats?.taken || 0}/7 jours</span>
                </div>
                <Progress 
                  value={stats.weekStats?.adherenceRate || 0} 
                  className="h-2 bg-white/10"
                />
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-pink-200">Ce mois</span>
                  <span className="text-white font-medium">{stats.monthStats?.taken || 0}/30 jours</span>
                </div>
                <Progress 
                  value={stats.monthStats?.adherenceRate || 0} 
                  className="h-2 bg-white/10"
                />
              </div>
            </div>

            {/* Adherence Message */}
            <div className={`mt-4 p-3 rounded-lg ${
              stats.adherenceLevel === 'excellent' ? 'bg-green-500/20' :
              stats.adherenceLevel === 'good' ? 'bg-blue-500/20' :
              stats.adherenceLevel === 'fair' ? 'bg-yellow-500/20' :
              'bg-red-500/20'
            }`}>
              <p className="text-white text-sm font-medium">{stats.message}</p>
            </div>
          </Card>
        )}

        {/* History Dialog */}
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="bg-purple-900 border-purple-700 max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Historique des prises</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
                  <div key={`day-header-${i}`} className="text-center text-pink-300 text-xs font-medium py-1">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {history.slice(0, 28).map((day) => (
                  <div
                    key={day.date}
                    className={`aspect-square rounded-lg flex flex-col items-center justify-center ${
                      day.taken 
                        ? 'bg-green-500/60' 
                        : 'bg-white/10'
                    }`}
                  >
                    <span className="text-white text-xs font-medium">
                      {new Date(day.date).getDate()}
                    </span>
                    {day.taken && <Check className="w-3 h-3 text-white mt-0.5" />}
                  </div>
                ))}
              </div>
              
              <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-500/60"></div>
                  <span className="text-pink-200">Pris</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-white/10"></div>
                  <span className="text-pink-200">Manqué</span>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reminders */}
        {reminders.length > 0 && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-pink-300" />
              <h3 className="text-lg font-bold text-white">Rappels à venir</h3>
            </div>
            <div className="space-y-3">
              {reminders.slice(0, 5).map((reminder) => (
                <div 
                  key={reminder.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{reminder.icon}</span>
                    <div>
                      <h4 className="text-white font-medium text-sm">{reminder.title}</h4>
                      <p className="text-pink-200 text-xs">{reminder.message}</p>
                      <p className="text-pink-300 text-xs mt-1">
                        {new Date(reminder.scheduledFor).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => acknowledgeReminder(reminder.id)}
                    className="text-pink-300 hover:text-white hover:bg-white/10"
                  >
                    OK
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Health Modules */}
        {healthModules.length > 0 && (
          <>
            <h3 className="text-lg font-bold text-white mt-6">Modules Santé</h3>
            {healthModules.map((module) => (
              <Card 
                key={module.id}
                className="p-6 bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/15 transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${module.color}40` }}
                  >
                    {getModuleIcon(module.icon)}
                  </div>

                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{module.title}</h3>
                    <p className="text-pink-200 text-sm mb-3">{module.description}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-pink-300">
                      <span>{module.lessons} leçons</span>
                      <span>•</span>
                      <span>{module.duration}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </>
        )}

        {/* Quick Actions */}
        <Card className="p-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-md border-purple-400/30">
          <h3 className="text-white font-semibold mb-3">Actions rapides</h3>
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-auto py-3"
              onClick={() => window.location.href = '/calendar'}
            >
              <div className="flex flex-col items-center gap-1">
                <Calendar className="w-5 h-5" />
                <span className="text-xs">Calendrier</span>
              </div>
            </Button>
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-auto py-3"
              onClick={() => setShowContraceptionDialog(true)}
            >
              <div className="flex flex-col items-center gap-1">
                <Shield className="w-5 h-5" />
                <span className="text-xs">Contraception</span>
              </div>
            </Button>
          </div>
        </Card>
      </div>

      <BottomNav active="health" />
    </div>
  );
};

export default Health;
